import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { metricsApi, statsApi } from '../../services/api';
import usePageLoadingEffect from '../../hooks/usePageLoadingEffect';

const METRIC_LABELS = {
  inactive_products: 'Inactive Products',
  active_products: 'Active Products',
  has_normalized_brand: 'With Brand',
  has_normalized_color: 'With Color',
  has_normalized_category: 'With Category',
  has_embedding: 'With Embeddings',
  has_identical_match: 'With Matches',
  has_live_identical_match: 'Live Matches',
  orphan_products: 'No Relationships',
  pending_review_count: 'Pending Reviews',
};

// A retailer needs enough matched products for a share to mean anything. Below this, Live %
// is noise: prospects and inactive retailers hold 1-2 matched products and are structurally
// 0% (the metric counts only active-to-active pairs), so without a floor thirteen of them
// crowd the top of the "worst overlap" sort and bury the real signal — BeOne at 12% and
// Ballegooyen at 16%. They render as "—", the same as unmeasured.
const MIN_MATCHED_FOR_SHARE = 50;

// Share of a retailer's matched products that can actually serve a handoff right now.
// null (not 0) when there is too little to divide, or when the snapshot predates migration
// 133 — an unmeasured retailer must not render as a hard 0%.
function livePct(row) {
  const matched = row.has_identical_match;
  const live = row.has_live_identical_match;
  if (live === null || live === undefined || !matched || matched < MIN_MATCHED_FOR_SHARE) return null;
  // Non-active retailers are structurally 0% — the metric counts active-to-active pairs only.
  // Showing that as a red 0% invites someone to "fix" a retailer that is simply switched off.
  if (row.retailer_status && row.retailer_status !== 'active') return null;
  return (live / matched) * 100;
}

function formatLive(value) {
  return value === null || value === undefined ? '—' : value.toLocaleString();
}

// Thresholds are deliberately coarse — this column is a "who do I call first" sort, not a
// precise gauge. Observed 2026-08-19: BeOne 12%, Ballegooyen 16%, Berden 57%, Topsy 69%.
function liveShareColor(pct) {
  if (pct === null) return 'text.disabled';
  if (pct < 25) return 'error.main';
  if (pct < 50) return 'warning.main';
  return 'success.main';
}

function MetricCard({ label, value, change, isPositiveGood = true }) {
  const hasChange = change !== undefined && change !== null;
  const isPositive = hasChange && change > 0;
  const changeColor = isPositiveGood
    ? (isPositive ? 'success.main' : 'error.main')
    : (isPositive ? 'warning.main' : 'success.main');

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h4">
          {value?.toLocaleString() ?? '-'}
        </Typography>
        {hasChange && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            {isPositive ? (
              <TrendingUp sx={{ color: changeColor, fontSize: 20 }} />
            ) : (
              <TrendingDown sx={{ color: changeColor, fontSize: 20 }} />
            )}
            <Typography variant="body2" sx={{ color: changeColor }}>
              {isPositive ? '+' : ''}{change.toLocaleString()}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default function MetricsDashboard() {
  const [filters, setFilters] = useState({
    retailer_id: '',
    normalized_brand: '',
    days: 30,
  });
  const [currentSnapshot, setCurrentSnapshot] = useState(null);
  const [history, setHistory] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [retailerSnapshots, setRetailerSnapshots] = useState([]);
  // Default sort: worst Live % first — the table's job is "who do I call about overlap".
  const [sortByLiveShare, setSortByLiveShare] = useState(false);
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  usePageLoadingEffect(loading);
  const [error, setError] = useState(null);

  const loadRetailers = useCallback(async () => {
    try {
      const data = await statsApi.getRetailers();
      setRetailers(data);
    } catch (err) {
      console.error('Failed to load retailers:', err);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [current, historyData, retailerData] = await Promise.all([
        metricsApi.getCurrentMetrics(filters),
        metricsApi.getHistory(filters),
        filters.retailer_id ? Promise.resolve([]) : metricsApi.getRetailerSnapshots(),
      ]);

      setCurrentSnapshot(current);
      setHistory(historyData);
      if (!filters.retailer_id) {
        setRetailerSnapshots(retailerData);
      }

      // Auto-compare today vs yesterday if we have at least 2 data points
      if (historyData.length >= 2) {
        const yesterday = historyData[historyData.length - 2];
        const today = historyData[historyData.length - 1];
        const changes = {};

        Object.keys(METRIC_LABELS).forEach((metric) => {
          const prevVal = yesterday[metric] || 0;
          const currVal = today[metric] || 0;
          changes[metric] = currVal - prevVal;
        });

        // Calculate inactive_products change from total - active
        const yesterdayInactive = (yesterday.total_products || 0) - (yesterday.active_products || 0);
        const todayInactive = (today.total_products || 0) - (today.active_products || 0);
        changes.inactive_products = todayInactive - yesterdayInactive;

        setComparison(changes);
      } else {
        setComparison(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadRetailers();
  }, [loadRetailers]);

  useEffect(() => {
    loadData();
  }, [loadData]);


  const handleRetailerClick = (retailerId) => {
    setFilters((prev) => ({ ...prev, retailer_id: retailerId }));
  };

  if (loading && !currentSnapshot) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const sortedRetailerSnapshots = sortByLiveShare
    ? [...retailerSnapshots].sort((a, b) => {
        const pa = livePct(a);
        const pb = livePct(b);
        if (pa === null && pb === null) return 0;
        if (pa === null) return 1;   // unmeasured sinks to the bottom, never reads as "worst"
        if (pb === null) return -1;
        return pa - pb;
      })
    : retailerSnapshots;

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, gap: { xs: 1, sm: 0 } }}>
        <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }} />

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 } }}>
          <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 150 } }}>
            <InputLabel>Retailer</InputLabel>
            <Select
              value={filters.retailer_id}
              label="Retailer"
              onChange={(e) => setFilters((prev) => ({ ...prev, retailer_id: e.target.value }))}
            >
              <MenuItem value="">All Retailers</MenuItem>
              {retailers.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 100 } }}>
            <InputLabel>Days</InputLabel>
            <Select
              value={filters.days}
              label="Days"
              onChange={(e) => setFilters((prev) => ({ ...prev, days: e.target.value }))}
            >
              <MenuItem value={7}>7 days</MenuItem>
              <MenuItem value={14}>14 days</MenuItem>
              <MenuItem value={30}>30 days</MenuItem>
              <MenuItem value={60}>60 days</MenuItem>
              <MenuItem value={90}>90 days</MenuItem>
            </Select>
          </FormControl>

          <Button
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {filters.retailer_id && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={`Filtered: ${retailers.find((r) => r.id === filters.retailer_id)?.name || filters.retailer_id}`}
            onDelete={() => setFilters((prev) => ({ ...prev, retailer_id: '' }))}
          />
        </Box>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard
            label="Active Products"
            value={currentSnapshot?.active_products}
            change={comparison?.active_products}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard
            label="Inactive Products"
            value={currentSnapshot?.total_products && currentSnapshot?.active_products
              ? currentSnapshot.total_products - currentSnapshot.active_products
              : null}
            change={comparison?.inactive_products}
            isPositiveGood={false}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard
            label="With Embeddings"
            value={currentSnapshot?.has_embedding}
            change={comparison?.has_embedding}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard
            label="With Matches"
            value={currentSnapshot?.has_identical_match}
            change={comparison?.has_identical_match}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard
            label="Live Matches"
            value={currentSnapshot?.has_live_identical_match}
            change={comparison?.has_live_identical_match}
          />
        </Grid>
      </Grid>

      {/* The two match metrics answer different questions and are meant to be read as a
          pair — see migration 133. Without this note "With Matches" reads as the health
          number, which is how a 20% rise coexisted with a 30% fall in overlays shown. */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        <strong>With Matches</strong> counts products holding an IDENTICAL edge — catalogue
        structure, blind to stock. <strong>Live Matches</strong> counts those that can serve a
        handoff right now: an in-stock size whose partner has that same size sold out. Both
        falling means a matching problem; only Live falling means a stock/seasonal one.
        Values before 2026-08-19 are reconstructed from stock history and are approximate.
      </Typography>

      {/* Trend Chart */}
      {history.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Pipeline Progress (Last {filters.days} Days)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={history}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="snapshot_date"
                  tickFormatter={(date) =>
                    new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  }
                />
                <YAxis />
                <Tooltip
                  labelFormatter={(date) =>
                    new Date(date).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                    })
                  }
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="active_products"
                  name="Active"
                  stroke="#8884d8"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="has_normalized_brand"
                  name="With Brand"
                  stroke="#82ca9d"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="has_embedding"
                  name="With Embeddings"
                  stroke="#ffc658"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="has_identical_match"
                  name="With Matches"
                  stroke="#ff7300"
                  strokeWidth={2}
                />
                {/* Live matches: products that can ACTUALLY serve a handoff (in-stock size
                    whose IDENTICAL partner has that same size sold out). connectNulls is
                    deliberately OFF — days before migration 133 hold NULL and must render as
                    a gap, never as a drop to zero. */}
                <Line
                  type="monotone"
                  dataKey="has_live_identical_match"
                  name="Live Matches"
                  stroke="#2e7d32"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {history.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No historical data available. Snapshots are captured automatically daily at 6:00 AM.
        </Alert>
      )}

      {/* Per-Retailer Table (only when viewing aggregate) */}
      {!filters.retailer_id && retailerSnapshots.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1">
              Per-Retailer Overview
            </Typography>
            {/* The two match columns can legitimately disagree, and With Matches can even
                exceed Active: it is a Neo4j count refreshed by the nightly ETL, so a same-day
                bulk deactivation shows up there a run late. Live filters on current Postgres
                status and is not affected. Saying so here stops it reading as a data bug. */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              <strong>Live</strong> = products that can serve a handoff now (in-stock size whose
              partner has that same size sold out). <strong>Live %</strong> is Live ÷ With Matches —
              sort by it to see which partners have the least usable overlap. With Matches reflects
              last night's graph, so a same-day deactivation sweep can briefly push it above Active.
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Retailer</TableCell>
                    <TableCell align="right">Active</TableCell>
                    <TableCell align="right">With Brand</TableCell>
                    <TableCell align="right">With Embeddings</TableCell>
                    <TableCell align="right">With Matches</TableCell>
                    <TableCell align="right">Live</TableCell>
                    <TableCell
                      align="right"
                      onClick={() => setSortByLiveShare((v) => !v)}
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Sort by lowest Live %"
                    >
                      Live %{sortByLiveShare ? ' ↑' : ''}
                    </TableCell>
                    <TableCell align="right">No Relations</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRetailerSnapshots.map((row) => (
                    <TableRow
                      key={row.retailer_id}
                      hover
                      onClick={() => handleRetailerClick(row.retailer_id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{row.retailer_name || row.retailer_id}</TableCell>
                      <TableCell align="right">{row.active_products?.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.has_normalized_brand?.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.has_embedding?.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.has_identical_match?.toLocaleString()}</TableCell>
                      <TableCell align="right">{formatLive(row.has_live_identical_match)}</TableCell>
                      <TableCell align="right" sx={{ color: liveShareColor(livePct(row)), fontWeight: 500 }}>
                        {livePct(row) === null ? '—' : `${Math.round(livePct(row))}%`}
                      </TableCell>
                      <TableCell align="right">{row.orphan_products?.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

    </Box>
  );
}
